import { Pen, Plus, Eye, Power, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Button, DataTable, Badge } from "../../../components/ui";
import { TableActions } from "../../../components/ui/TableActions";
import { TABLE_BACKGROUNDS } from "../../../constants/tableBackgrounds";
import AddRoomModal from "../modals/AddRoomModal";
import { settingsRoomService } from "../services/settingsRoomService";

export default function RoomRequirementTab() {
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRoom, setEditingRoom] = useState(null);
  const [viewingRoom, setViewingRoom] = useState(null);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const res = await settingsRoomService.getRooms();
      if (res.success) {
        setRooms(res.data);
      }
    } catch (error) {
      toast.error("Failed to fetch rooms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const openCreate = () => {
    setEditingRoom(null);
    setViewingRoom(null);
    setModalOpen(true);
  };

  const openEdit = (room) => {
    setEditingRoom(room);
    setViewingRoom(null);
    setModalOpen(true);
  };

  const openView = (room) => {
    setEditingRoom(null);
    setViewingRoom(room);
    setModalOpen(true);
  };

  const toggleDisable = async (id) => {
    const roomToToggle = rooms.find(r => r._id === id);
    if (!roomToToggle) return;

    try {
      const newStatus = roomToToggle.status === 'Active' ? 'Inactive' : 'Active';
      const res = await settingsRoomService.updateRoom(id, { status: newStatus });
      if (res.success) {
        toast.success(`Room marked as ${newStatus}`);
        setRooms(rooms.map(r => r._id === id ? { ...r, status: newStatus } : r));
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const saveRoom = async (roomData) => {
    try {
      if (editingRoom) {
        const res = await settingsRoomService.updateRoom(editingRoom._id, roomData);
        if (res.success) {
          toast.success("Room updated successfully");
          setRooms(rooms.map(r => r._id === editingRoom._id ? res.data : r));
        }
      } else {
        const res = await settingsRoomService.createRoom(roomData);
        if (res.success) {
          toast.success("Room created successfully");
          setRooms([res.data, ...rooms]);
        }
      }
      setModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Operation failed");
    }
  };

  const filteredRooms = rooms.filter(room => 
    (room.name || "").toLowerCase().includes(search.toLowerCase()) || 
    (room.code || "").toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      header: "Room ID",
      render: (item) => <span className="text-[11px] font-bold text-slate-700 tracking-wider uppercase">{item.roomId || "—"}</span>
    },
    {
      header: "Room Code",
      render: (item) => <span className="text-[11px] font-bold text-slate-700 tracking-wider uppercase">{item.code || "—"}</span>
    },
    {
      header: "Room Name",
      render: (item) => <span className="font-semibold text-slate-800 text-[12px] truncate block max-w-[200px]" title={item.name}>{item.name || "—"}</span>
    },
    {
      header: "Description",
      render: (item) => <span className="text-[12px] font-medium text-slate-600 truncate block max-w-[200px]" title={item.description}>{item.description || "—"}</span>
    },
    {
      header: "Status",
      render: (item) => {
        const isActive = item.status === "Active";
        return (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${isActive
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200/30'
                : 'bg-slate-50 text-slate-500 border-slate-200/30'
              }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
            {item.status}
          </span>
        );
      }
    },
    {
      header: "Actions",
      className: "text-center w-32",
      render: (item) => {
        const isInactiveStatus = item.status === "Inactive";
        return (
          <TableActions
            onView={() => openView(item)}
            onEdit={() => openEdit(item)}
            onToggle={() => toggleDisable(item._id)}
            isActive={item.status === 'Active'}
            disabled={isInactiveStatus}
          />
        );
      }
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-black text-slate-800 tracking-tight leading-none mb-2">
            Room Requirements
          </h3>
          <p className="text-[13px] text-slate-500 font-medium">
            Manage your room requirements and descriptions
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#1e293b] text-white hover:bg-black"
          size="md"
          leftIcon={<Plus size={16} />}
        >
          Add Room
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search rooms..."
            className="w-full h-9 pl-9 pr-4 text-xs bg-slate-50 border border-slate-200 rounded-md outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
          />
          <svg className="absolute left-3 top-2.5 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
        </div>
      </div>

      <div className="card overflow-hidden relative min-h-[200px]">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-white/50 backdrop-blur-sm z-10">
            <Loader2 className="w-6 h-6 animate-spin text-brand-blue" />
          </div>
        ) : null}
        <DataTable
          columns={columns}
          data={filteredRooms}
          backgroundImage={TABLE_BACKGROUNDS.teams}
          isRowInactive={(row) => row.status === 'Inactive'}
          getRowClassName={(row) => row.status === 'Inactive' ? 'bg-slate-100/90 border-y border-slate-200/80' : 'hover:bg-slate-50/50'}
        />
      </div>

      {modalOpen && (
        <AddRoomModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={saveRoom}
          initialData={editingRoom || viewingRoom}
          isViewOnly={!!viewingRoom}
          existingRooms={rooms}
        />
      )}
    </div>
  );
}




